import { Plugin, PluginKey } from 'hubspot-prosemirror/pm/state';
import { Decoration, DecorationSet } from 'hubspot-prosemirror/pm/view';
import { createRoot } from 'react-dom/client';
import ReactDOMServer from 'react-dom/server';
import debounce from 'transmute/debounce';
import Raven from 'raven-js';
import { Image } from 'foundations-components/transitional/components/Image';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import styled from 'styled-components';
import { GenericNotFound } from '../../components/GenericNotFound';
import { insertSlice, insertContent, hasAlignmentInHtml, isAtParagraphStart } from '../../utils/documentUtils';
import uuid from '../../utils/uuid';
import { getMentionNodeType, getMentionTrigger } from './suggestionsUtil';
import { INSERT_EMOJI } from 'rich-text-lib/constants/usageTracking';
import { PM_EMOJI } from '../../constants/events';
import { convertToPlaintextState } from '../../utils/plaintextUtils';
import { Tracker } from '../../../utils/usageTracker';
import { AtMentionNodeView } from './AtMentionNodeView';
import { DefaultAtMentionComponent } from './DefaultAtMentionComponent';
import { View } from 'foundations-components/components/View';
import { callIfPossible } from 'foundations-components/transitional/utils/functions';
import { Flex } from 'foundations-components/components/Flex';

// Styled components to replace CSS classes
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledSuggestionsList = styled.div.withConfig({
  displayName: "MentionsPlugin__StyledSuggestionsList"
})(["width:100%;background-color:var(--trellis-color-fill-surface-default);border-radius:4px;[data-suggestion-item='true']{padding:8px;cursor:pointer;&::before{border-top:1px solid;}&[data-suggestion-disabled='true']{color:var(--trellis-color-text-core-disabled);cursor:not-allowed;}}[data-suggestion-active='true']{background-color:var(--trellis-color-fill-field-hover);}"]);
const StyledSuggestionsFooter = styled.div.withConfig({
  displayName: "MentionsPlugin__StyledSuggestionsFooter"
})(["padding:var(--trellis-spacing-200);background-color:var(--trellis-color-fill-surface-default);border-top:1px solid var(--trellis-color-border-core-default);border-radius:0 0 4px 4px;&[data-suggestion-active='true']{background-color:var(--trellis-color-fill-field-hover);}"]);
const ACTIVE_ATTR = 'data-suggestion-active';

// When allowSpace is on, the query may span multiple words to support
// multi-word names (e.g. "The New York Public Library"). The count is capped
// because getMatch scans from the paragraph start to the cursor: an unbounded
// pattern lets a stale trigger earlier in the paragraph keep matching every
// subsequent word, re-surfacing the suggestion popover indefinitely.
const MAX_EXTRA_QUERY_WORDS = 4;
export function getRegexp(trigger, allowSpace, canSearch) {
  if (!canSearch) {
    return new RegExp(`(^|\\s)${trigger}$`);
  }
  // Re-anchor the mention on the trigger nearest the cursor. A trigger
  // preceded by whitespace starts a new query (so "@Alice @Bob" searches
  // "Bob"), while a trigger with no preceding space stays inside the query, so
  // email-style handles like "@alice@example.com" are preserved. The negative
  // lookahead stops a query word from spanning a space-preceded later trigger,
  // which previously polluted the search with earlier unresolved mentions
  // (ProductSupport#169365). Only the multi-word (allowSpace) branch can span a
  // later trigger, so the single-word branch is unchanged.
  return allowSpace ? new RegExp(`(^|\\s)${trigger}(\\S+(?:\\s(?!${trigger})\\S+){0,${MAX_EXTRA_QUERY_WORDS}})$`) : new RegExp(`(^|\\s)${trigger}([\\S]+)$`);
}

/**
 * @param {ResolvedPos} $position https://prosemirror.net/docs/ref/#model.Resolved_Positions
 * @param {JSONObject} opts
 * @returns {JSONObject | undefined} undefined if no match
 */
export function getMatch($position, opts) {
  if ($position.depth === 0) {
    return undefined;
  }

  // take current para text content upto cursor start.
  // this makes the regex simpler and parsing the matches easier.
  const parastart = $position.before();
  const text = $position.doc.textBetween(parastart, $position.pos, '\n', '\0');
  const regex = getRegexp(opts.trigger, opts.allowSpace, opts.canSearch);
  const match = text.match(regex);

  // if match found, return match with useful information.
  if (match && match.index !== undefined) {
    // adjust match.index to remove the matched extra space
    match.index = match[0].startsWith(' ') ? match.index + 1 : match.index;
    match[0] = match[0].startsWith(' ') ? match[0].substring(1, match[0].length) : match[0];

    // The absolute position of the match in the document
    const from = $position.start() + match.index;
    const to = from + match[0].length;
    const queryText = match[2];
    return {
      range: {
        from,
        to
      },
      queryText
    };
  }
  return undefined;
}
export function isNearBottom({
  scrollTop,
  clientHeight,
  scrollHeight,
  threshold
}) {
  return scrollTop + clientHeight >= scrollHeight - threshold;
}
function dedupeById(items) {
  const seen = new Set();
  return items.filter(item => {
    if (seen.has(item.id)) {
      return false;
    }
    seen.add(item.id);
    return true;
  });
}
const getNewState = function getNewState() {
  return {
    active: false,
    range: {
      from: 0,
      to: 0
    },
    text: '',
    suggestions: [],
    index: 0,
    // current active suggestion index
    offset: 0,
    hasMore: false,
    isLoadingMore: false,
    isFooterActive: false
  };
};
export function GenericSuggestionsComponent(props) {
  const {
    suggestions,
    NotFoundComponent,
    text,
    isLoadingMore
  } = props;
  return (
    /*#__PURE__*/
    // @bend-expect trellis-tokenized
    _jsxs(StyledSuggestionsList, {
      "data-suggestion-item-list": "true",
      children: [!suggestions || !suggestions.length ? NotFoundComponent ? /*#__PURE__*/_jsx(View, {
        padding: "500",
        as: "div",
        children: /*#__PURE__*/_jsx(NotFoundComponent, {
          text: text
        })
      }) : null : suggestions.map((item, key) =>
      /*#__PURE__*/
      // tabIndex makes this element focusable, allowing it to be the relatedTarget of a blur event
      _jsx(View, {
        padding: "200",
        cursor: "pointer",
        tabIndex: 0,
        "data-suggestion-item": "true",
        "data-suggestion-disabled": item.disabled,
        children: /*#__PURE__*/_jsxs(Flex, {
          alignItems: "center",
          children: [item.imageUrl ? /*#__PURE__*/_jsx(Image, {
            src: item.imageUrl,
            width: 24,
            height: 24,
            marginInlineEnd: "200",
            display: "inline-block",
            shape: "circle",
            responsive: false
          }) : null, item.searchText || item.name]
        })
      }, key)), isLoadingMore ? /*#__PURE__*/_jsx(View, {
        padding: "200",
        as: "div",
        "data-suggestion-loading": "true",
        children: /*#__PURE__*/_jsx(Flex, {
          alignItems: "center",
          justifyContent: "center",
          children: /*#__PURE__*/_jsx(LoadingSpinner, {
            size: "extra-small"
          })
        })
      }) : null]
    })
  );
}
export function getMentionsPlugin(pluginOptions) {
  var _opts$pagination$page, _opts$pagination, _opts$pagination$load, _opts$pagination2;
  // TODO: Add empty data error state - https://git.hubteam.com/HubSpot/customer-data-rte/pull/213#discussion_r1593499
  const opts = Object.assign({
    allowSpace: true,
    maxNoOfSuggestions: 20,
    delay: 250,
    getSuggestionsComponent: GenericSuggestionsComponent,
    NotFoundComponent: GenericNotFound,
    canSearch: true
  }, pluginOptions, {
    trigger: getMentionTrigger(pluginOptions.type)
  });
  if (!opts.pagination && !opts.getSuggestions) {
    throw new Error(`[MentionsPlugin] type "${opts.type}" requires either getSuggestions or pagination to be provided.`);
  }
  const pageSize = (_opts$pagination$page = (_opts$pagination = opts.pagination) === null || _opts$pagination === void 0 ? void 0 : _opts$pagination.pageSize) !== null && _opts$pagination$page !== void 0 ? _opts$pagination$page : opts.maxNoOfSuggestions;
  const loadMoreThreshold = (_opts$pagination$load = (_opts$pagination2 = opts.pagination) === null || _opts$pagination2 === void 0 ? void 0 : _opts$pagination2.loadMoreThreshold) !== null && _opts$pagination$load !== void 0 ? _opts$pagination$load : 48;

  // Declared up front so showList's scroll handler and loadPage (below) can
  // reference it without tripping no-use-before-define.
  const pluginKey = new PluginKey(`${opts.type}_${uuid()}_autosuggestions`);

  // The active editor view, tracked by showList so the once-wired scroll
  // handler can read plugin state and request the next page.
  let currentView;
  let scrollHandler;

  // Track whether the popover was explicitly dismissed (blur/escape).
  // Prevents debounced search callback from re-showing the popover after dismissal.
  // Reset on focus or when user types new text or starts a new trigger at a different position.
  let dismissed = false;
  // Track whether the plugin has been destroyed. Prevents a pending debounced
  // search from re-appending the dropdown element after destroy() removes it,
  // which would leave an orphaned popover in the DOM.
  let destroyed = false;
  // Tracks previous search text value. When text changes, reset dismissed
  // flag to allow popover to reappear (user is continuing their search).
  let lastText = '';
  // Tracks previous trigger position. When position changes, reset dismissed
  // flag to allow popover at new location (e.g., new line/paragraph).
  let lastRange = {
    from: 0,
    to: 0
  };
  // dropdown element
  const el = document.createElement('div');
  el.setAttribute('data-prosemirror-mention-dropdown', pluginOptions.type);
  el.style.overflowY = 'auto';
  const suggestionsScrollContainer = opts.suggestionsFooter ? document.createElement('div') : el;
  const footerContainer = opts.suggestionsFooter ? document.createElement('div') : undefined;
  let footerRoot;
  if (footerContainer) {
    suggestionsScrollContainer.setAttribute('data-suggestion-scroll-container', 'true');
    footerContainer.style.flexShrink = '0';
    el.style.flexDirection = 'column';
    el.style.overflowY = 'hidden';
    suggestionsScrollContainer.style.flex = '1 1 auto';
    suggestionsScrollContainer.style.minHeight = '0';
    suggestionsScrollContainer.style.overflowY = 'auto';
    el.append(suggestionsScrollContainer, footerContainer);
  }
  const getItemAtIndex = function getItemAtIndex(index) {
    const itemListParent = suggestionsScrollContainer.querySelector('[data-suggestion-item-list="true"]');
    const itemList = itemListParent ? itemListParent.childNodes : [];
    return itemList[index];
  };
  const setActive = element => element === null || element === void 0 ? void 0 : element.setAttribute(ACTIVE_ATTR, 'true');
  const clearActive = element => element === null || element === void 0 ? void 0 : element.removeAttribute(ACTIVE_ATTR);
  const getFooterElement = function getFooterElement() {
    return footerContainer === null || footerContainer === void 0 ? void 0 : footerContainer.querySelector('[data-suggestion-footer="true"]');
  };
  const addActiveStateAtIndex = index => setActive(getItemAtIndex(index));
  const removeActiveStateAtIndex = index => clearActive(getItemAtIndex(index));
  const addFooterActiveState = () => setActive(getFooterElement());
  const removeFooterActiveState = () => clearActive(getFooterElement());
  const setIndex = function setIndex(index, state) {
    if (state.isFooterActive) {
      removeFooterActiveState();
    } else {
      removeActiveStateAtIndex(state.index);
    }
    state.isFooterActive = false;
    state.index = index;
    addActiveStateAtIndex(state.index);
  };
  const setFooterActive = function setFooterActive(state) {
    removeActiveStateAtIndex(state.index);
    state.isFooterActive = true;
    addFooterActiveState();
  };
  const hideList = function hideList() {
    dismissed = true;
    const wasVisible = el.style.display !== 'none' && el.style.display;
    if (wasVisible) {
      callIfPossible(pluginOptions.onClosePopover, {
        type: 'inline'
      });
    }
    el.style.display = 'none';
  };
  const select = function select(view, state) {
    if (state.isFooterActive) {
      var _opts$suggestionsFoot, _opts$suggestionsFoot2;
      hideList();
      (_opts$suggestionsFoot = opts.suggestionsFooter) === null || _opts$suggestionsFoot === void 0 || (_opts$suggestionsFoot2 = _opts$suggestionsFoot.onActivate) === null || _opts$suggestionsFoot2 === void 0 || _opts$suggestionsFoot2.call(_opts$suggestionsFoot);
      return;
    }
    const item = state.suggestions[state.index];
    if (!item || item.disabled) return;
    hideList();
    if (opts.fetchSelectSuggestion) {
      opts.fetchSelectSuggestion({
        snippet: item,
        handleInsert: ({
          html
        }) => {
          let nextState;
          const hasAlignment = hasAlignmentInHtml(html);
          const $from = view.state.doc.resolve(state.range.from);
          const atParagraphStart = isAtParagraphStart($from);
          if (hasAlignment && atParagraphStart) {
            nextState = insertContent({
              htmlBody: html,
              editorState: view.state,
              from: state.range.from,
              to: state.range.to
            });
          } else {
            nextState = insertSlice({
              editorState: view.state,
              htmlBody: html,
              $from,
              $to: view.state.doc.resolve(state.range.to)
            });
          }
          nextState = pluginOptions.usePlaintextMode ? convertToPlaintextState(nextState, pluginOptions.plaintextOptions) : nextState;

          /**
           * updateState and dispatch cannot be called with conflicting states,
           * so they should both be called with the same newState.
           */
          view.updateState(nextState);
          // dispatch to register changed state
          view.dispatch(nextState.tr);
        }
      });
      return;
    }
    let tr;
    if (opts.type === 'EMOJI') {
      Tracker.track(PM_EMOJI, {
        action: INSERT_EMOJI,
        method: 'shortcut',
        value: item.id
      });
      tr = view.state.tr.insertText(item.text, state.range.from, state.range.to);
    } else {
      const attrs = {
        // we want to insert the actual text of the item, not the search text
        name: item.text || item.searchText || item.name,
        id: item.id,
        display: item.display || null
      };
      let marks;
      const cursor = view.state.selection.$cursor;
      if (cursor) {
        marks = cursor.marks();
      }
      let node = getMentionNodeType(view.state.schema, opts.type).create(attrs);
      if (marks) {
        node = node.mark(marks);
      }
      tr = view.state.tr.replaceWith(state.range.from, state.range.to, node);
    }
    view.dispatch(tr);
  };
  const safeNodeAtPos = (view, pos) => {
    try {
      const ret = view.domAtPos(pos);
      if (!ret.node.querySelector) {
        return ret.node.parentNode;
      }
      return ret.node;
    } catch (e) {
      // possibly future solution, make this recursive to find a different pos if it's out of bounds?
      // return safeDomAtPos(view, pos - 1);
      return undefined;
    }
  };
  const showList = function showList(view, state, suggestions, {
    preserveScroll = false
  } = {}) {
    var _opts$suggestionsFoot3, _pluginOptions$leftOf, _window$visualViewpor, _window$visualViewpor2;
    currentView = view;
    if (destroyed) {
      return;
    }
    // Don't show if explicitly dismissed (blur/escape) - prevents debounced
    // callback from re-showing popover after user intentionally closed it
    if (dismissed) {
      return;
    }
    // Rebuilding innerHTML resets scrollTop to 0; capture it so appended pages
    // (infinite scroll) don't yank the user back to the top of the list.
    const prevScrollTop = suggestionsScrollContainer.scrollTop;
    // eslint-disable-next-line no-restricted-syntax
    suggestionsScrollContainer.innerHTML = ReactDOMServer.renderToString(opts.getSuggestionsComponent({
      suggestions,
      text: state.text,
      NotFoundComponent: opts.NotFoundComponent,
      isLoadingMore: state.isLoadingMore
    }));
    if (suggestions && suggestions.length) {
      suggestionsScrollContainer.querySelectorAll('[data-suggestion-item="true"]').forEach((itemNode, index) => {
        itemNode.addEventListener('mousedown', event => {
          event.preventDefault();
          event.stopPropagation();
        });
        itemNode.addEventListener('click', event => {
          event.preventDefault();
          event.stopPropagation();
          view.focus();
          select(view, state);
        });
        itemNode.addEventListener('mouseover', () => {
          setIndex(index, state);
        });
        itemNode.addEventListener('mouseout', () => {
          setIndex(index, state);
        });
      });

      // highlight first element by default - like Facebook.
      if (state.isFooterActive) {
        addFooterActiveState();
      } else {
        addActiveStateAtIndex(state.index);
      }
    } else if ((_opts$suggestionsFoot3 = opts.suggestionsFooter) !== null && _opts$suggestionsFoot3 !== void 0 && _opts$suggestionsFoot3.onActivate) {
      state.isFooterActive = true;
      addFooterActiveState();
    }

    // get current @mention span left and top.
    const node = safeNodeAtPos(view, view.state.selection.$from.pos);
    if (!node) return;
    const textDOM = node.querySelector('[data-suggestion-text="true"]');
    const offset = textDOM && textDOM.getBoundingClientRect();
    if (!offset) {
      el.style.display = 'none';
      return;
    }
    document.body.appendChild(el);
    const MAX_HEIGHT = 200;
    el.style.position = 'fixed';
    if (pluginOptions.width) {
      el.style.width = `${pluginOptions.width}px`;
    } else {
      el.style.maxWidth = '400px';
    }
    el.style.maxHeight = `${MAX_HEIGHT}px`;
    el.style.zIndex = '9999';
    el.style.border = `1px solid var(--trellis-color-border-core-default)`;
    el.style.boxShadow = '0 3px 8px rgba(0, 0, 0, .1)';
    el.style.left = `${(_pluginOptions$leftOf = pluginOptions.leftOffset) !== null && _pluginOptions$leftOf !== void 0 ? _pluginOptions$leftOf : offset.left}px`;
    const wasHidden = el.style.display === 'none' || !el.style.display;
    el.style.display = opts.suggestionsFooter ? 'flex' : 'block';
    const {
      offsetHeight: textDOMHeight
    } = textDOM;

    // On mobile devices (e.g. iPad), when the on-screen keyboard appears, the visual viewport
    // shifts but getBoundingClientRect() returns values relative to the layout viewport.
    // Adding this offset corrects the dropdown position when the keyboard is showing.
    const viewportOffsetTop = (_window$visualViewpor = (_window$visualViewpor2 = window.visualViewport) === null || _window$visualViewpor2 === void 0 ? void 0 : _window$visualViewpor2.offsetTop) !== null && _window$visualViewpor !== void 0 ? _window$visualViewpor : 0;
    let top;
    if (opts.suggestionsPlacement === 'top') {
      // Place above trigger text
      top = offset.top - el.offsetHeight - 10 + viewportOffsetTop;
    } else if (opts.suggestionsPlacement === 'bottom') {
      // Place below trigger text
      top = textDOMHeight + offset.top + 10 + viewportOffsetTop;
    } else {
      // Smart placement: show below by default, above if near bottom edge
      const onBottomEdge = window.innerHeight - offset.bottom < MAX_HEIGHT + 50;
      top = onBottomEdge ? offset.top - el.offsetHeight - 10 + viewportOffsetTop : textDOMHeight + offset.top + 10 + viewportOffsetTop;
    }
    el.style.top = `${top}px`;
    if (wasHidden) {
      callIfPossible(pluginOptions.onOpenPopover, {
        type: 'inline'
      });
    }
    if (preserveScroll) {
      suggestionsScrollContainer.scrollTop = prevScrollTop;
    }
  };
  const scrollActiveItemIntoView = function scrollActiveItemIntoView(index) {
    const activeItem = getItemAtIndex(index);
    if (!activeItem) return;
    const containerRect = suggestionsScrollContainer.getBoundingClientRect();
    const itemRect = activeItem.getBoundingClientRect();
    if (itemRect.top < containerRect.top) {
      suggestionsScrollContainer.scrollTop -= containerRect.top - itemRect.top;
    } else if (itemRect.bottom > containerRect.bottom) {
      suggestionsScrollContainer.scrollTop += itemRect.bottom - containerRect.bottom;
    }
  };
  const scrollLoadingRowIntoView = function scrollLoadingRowIntoView() {
    const loadingRow = suggestionsScrollContainer.querySelector('[data-suggestion-loading="true"]');
    if (!loadingRow) return;
    const containerRect = suggestionsScrollContainer.getBoundingClientRect();
    const rowRect = loadingRow.getBoundingClientRect();
    if (rowRect.bottom > containerRect.bottom) {
      suggestionsScrollContainer.scrollTop += rowRect.bottom - containerRect.bottom;
    }
  };
  const loadPage = (view, state, {
    reset,
    advanceIndex = false
  }) => {
    var _state$offset;
    const {
      pagination
    } = opts;
    if (destroyed || dismissed || !pagination) {
      return;
    }
    // For follow-up pages, bail when nothing more is available or a request is
    // already in flight.
    if (!reset && (!state.hasMore || state.isLoadingMore)) {
      return;
    }
    const query = state.text;
    const offset = reset ? 0 : (_state$offset = state.offset) !== null && _state$offset !== void 0 ? _state$offset : 0;
    state.isLoadingMore = true;

    // Render the in-progress spinner beneath the loaded results. On a reset we
    // wait for the first page before rendering, matching the non-paginated path.
    if (!reset) {
      showList(view, state, state.suggestions, {
        preserveScroll: true
      });
      // The spinner is appended below the last row, i.e. below the bottom the
      // user just scrolled to, so preserveScroll leaves it off-screen. Nudge it
      // into view so a scroll-triggered load is actually visible. Keyboard
      // paging (advanceIndex) follows its own highlight, so leave it be.
      if (!advanceIndex) {
        scrollLoadingRowIntoView();
      }
    }
    pagination.fetchSuggestions({
      query,
      offset,
      count: pageSize
    }, ({
      suggestions,
      hasMore
    }) => {
      const liveState = pluginKey.getState(view.state);
      // Discard stale responses: the query moved on, or the popover was
      // dismissed/destroyed while the page was in flight.
      if (destroyed || dismissed || !liveState || liveState.text !== query) {
        return;
      }
      const prevLength = liveState.suggestions.length;
      const merged = reset ? dedupeById(suggestions) : dedupeById([...liveState.suggestions, ...suggestions]);
      liveState.suggestions = merged;
      liveState.offset = offset + suggestions.length;
      // An empty page can't advance the offset, so stop paginating even if the
      // source still reports more; otherwise the same offset is refetched on
      // every later scroll/arrow.
      liveState.hasMore = hasMore && suggestions.length > 0;
      liveState.isLoadingMore = false;
      if (reset) {
        liveState.index = 0;
        liveState.isFooterActive = false;
      } else if (advanceIndex && merged.length > prevLength) {
        liveState.index = prevLength;
        liveState.isFooterActive = false;
      }
      showList(view, liveState, merged, {
        preserveScroll: !reset
      });
      // Keyboard paging past the last item must follow the highlight to the
      // first new row; scroll-triggered loads keep the user's position.
      if (advanceIndex && merged.length > prevLength) {
        scrollActiveItemIntoView(liveState.index);
      }
    });
  };

  // Define the infinite-scroll handler once. The view lifecycle below binds it
  // to the scrolling results container and removes it on destroy. That element
  // persists across showList's innerHTML rebuilds, so the listener survives.
  if (opts.pagination) {
    scrollHandler = () => {
      if (!currentView) {
        return;
      }
      const liveState = pluginKey.getState(currentView.state);
      if (liveState && isNearBottom({
        scrollTop: suggestionsScrollContainer.scrollTop,
        clientHeight: suggestionsScrollContainer.clientHeight,
        scrollHeight: suggestionsScrollContainer.scrollHeight,
        threshold: loadMoreThreshold
      })) {
        loadPage(currentView, liveState, {
          reset: false
        });
      }
    };
  }
  const moveActive = function moveActive(view, state, direction) {
    var _opts$suggestionsFoot4;
    const lastIndex = state.suggestions.length - 1;
    const canActivateFooter = Boolean((_opts$suggestionsFoot4 = opts.suggestionsFooter) === null || _opts$suggestionsFoot4 === void 0 ? void 0 : _opts$suggestionsFoot4.onActivate);
    if (state.isFooterActive) {
      if (state.suggestions.length) {
        const target = direction === 1 ? 0 : lastIndex;
        setIndex(target, state);
        scrollActiveItemIntoView(target);
      }
      return;
    }
    if (!state.suggestions.length) {
      if (canActivateFooter) {
        setFooterActive(state);
      }
      return;
    }

    // When paginating, arrowing onto the last loaded item with more available
    // pulls the next page instead of wrapping to the top, so keyboard users can
    // page through every result. While that page is in flight, hold on the last
    // item rather than wrapping to the top and snapping back when it arrives.
    if (direction === 1 && opts.pagination && state.hasMore && state.index === lastIndex) {
      if (!state.isLoadingMore) {
        loadPage(view, state, {
          reset: false,
          advanceIndex: true
        });
      }
      return;
    }
    const atEdge = direction === 1 ? state.index === lastIndex : state.index === 0;
    if (canActivateFooter && atEdge) {
      setFooterActive(state);
      return;
    }
    removeActiveStateAtIndex(state.index);
    state.index += direction;
    if (state.index > lastIndex) {
      state.index = 0;
    } else if (state.index < 0) {
      state.index = lastIndex;
    }
    addActiveStateAtIndex(state.index);
    scrollActiveItemIntoView(state.index);
  };
  const goNext = (view, state) => moveActive(view, state, 1);
  const goPrev = (view, state) => moveActive(view, state, -1);
  const debouncedSearch = debounce(opts.delay, (state, view) => {
    var _opts$getSuggestions;
    if (opts.pagination) {
      loadPage(view, state, {
        reset: true
      });
      return;
    }
    (_opts$getSuggestions = opts.getSuggestions) === null || _opts$getSuggestions === void 0 || _opts$getSuggestions.call(opts, state.text, suggestions => {
      const slicedSuggestions = suggestions.slice(0, opts.maxNoOfSuggestions);
      state.suggestions = slicedSuggestions;
      showList(view, state, slicedSuggestions);
    });
  });
  const useAtMentionNodeViews = Boolean(pluginOptions.AtMentionComponent || pluginOptions.onAtMentionClick);
  const handleFooterMouseEnter = () => {
    var _opts$suggestionsFoot5;
    if (!currentView || !((_opts$suggestionsFoot5 = opts.suggestionsFooter) !== null && _opts$suggestionsFoot5 !== void 0 && _opts$suggestionsFoot5.onActivate)) {
      return;
    }
    const state = pluginKey.getState(currentView.state);
    if (state) {
      setFooterActive(state);
    }
  };
  const handleFooterClick = event => {
    const target = event.target;
    if (target instanceof Element && target.closest('a,button')) {
      hideList();
    }
  };
  const handleFooterFocusOut = event => {
    const nextTarget = event.relatedTarget;
    if (!(nextTarget instanceof Node) || !el.contains(nextTarget)) {
      hideList();
    }
  };

  /**
   * See https://prosemirror.net/docs/ref/#state.Plugin_System
   * for the plugin properties spec.
   */
  return new Plugin({
    /* FIXME: The `Plugin` class type does doesn't forward a type argument for this property;
       it should be `PluginKey<MentionsPluginState>`, instead it is `PluginKey<any>`. */
    key: pluginKey,
    // we will need state to track if suggestion dropdown is currently active or not
    state: {
      init() {
        return getNewState();
      },
      apply(tr, __state) {
        // compute state.active for current transaction and return
        const meta = tr.getMeta(pluginKey);
        if (meta) {
          return meta;
        }
        const newState = getNewState();
        const selection = tr.selection;
        if (selection.from !== selection.to) {
          return newState;
        }
        const $position = selection.$from;
        const match = getMatch($position, opts);

        // if match found update state
        if (match) {
          newState.active = true;
          newState.range = match.range;
          newState.text = match.queryText;
        }
        return newState;
      }
    },
    props: Object.assign({}, useAtMentionNodeViews && {
      nodeViews: {
        atmention: (node, view, getPos) => {
          return new AtMentionNodeView(node, view, getPos, {
            component: pluginOptions.AtMentionComponent || DefaultAtMentionComponent,
            onAtMentionClick: pluginOptions.onAtMentionClick
          });
        }
      }
    }, {
      // We'll need props to hi-jack keydown/keyup & enter events when suggestion dropdown
      // is active.
      handleKeyDown(view, e) {
        const state = pluginKey.getState(view.state);

        // don't handle if no suggestions or not in active mode
        if (!state || !state.active && !state.suggestions.length) {
          return false;
        }

        // if any of the below keys, override with custom handlers.
        const enter = e.key === 'Enter';
        const down = e.key === 'ArrowDown';
        const up = e.key === 'ArrowUp';
        const esc = e.key === 'Escape';
        const tab = e.key === 'Tab';
        if (down) {
          goNext(view, state);
          e.stopPropagation();
          return true;
        } else if (up) {
          goPrev(view, state);
          e.stopPropagation();
          return true;
        } else if (enter || tab) {
          select(view, state);
          e.stopPropagation();
          return true;
        } else if (esc) {
          hideList();
          const tr = view.state.tr;
          tr.setMeta(pluginKey, getNewState());
          e.stopPropagation();
          return true;
        } else {
          // didn't handle. handover to prosemirror for handling.
          return false;
        }
      },
      // to decorate the currently active @mention text in ui
      decorations(editorState) {
        const state = pluginKey.getState(editorState);
        if (!state) {
          return null;
        }
        const {
          active,
          range
        } = state;
        if (!active) return null;
        return DecorationSet.create(editorState.doc, [Decoration.inline(range.from, range.to, {
          nodeName: 'span',
          'data-suggestion-text': 'true'
        })]);
      },
      handleDOMEvents: {
        focus: () => {
          dismissed = false;
        },
        // hide the suggestion list when the editor is blurred
        blur: (view, event) => {
          try {
            const state = pluginKey.getState(view.state);
            const focusEvent = event;
            const focusedElement = focusEvent.relatedTarget;
            const didNotClickSuggestion = !focusedElement || !el.contains(focusedElement);
            if (state && state.active && didNotClickSuggestion) {
              hideList();
            }
          } catch (error) {
            Raven.captureException(error, {
              extra: {
                message: 'ProseMirror Mentions Plugin - Error handling blur event'
              }
            });
          }
        }
      }
    }),
    // To track down state mutations and add dropdown reactions
    view() {
      // Reset destroyed flag for each new view instance so a previously
      // destroyed plugin view doesn't permanently block showList().
      destroyed = false;
      if (footerContainer && opts.suggestionsFooter && !footerRoot) {
        var _opts$suggestionsFoot6;
        footerRoot = createRoot(footerContainer);
        footerRoot.render( /*#__PURE__*/_jsx(StyledSuggestionsFooter, {
          "data-suggestion-footer": "true",
          children: (_opts$suggestionsFoot6 = opts.suggestionsFooter) === null || _opts$suggestionsFoot6 === void 0 ? void 0 : _opts$suggestionsFoot6.content
        }));
        footerContainer.addEventListener('mouseenter', handleFooterMouseEnter);
        footerContainer.addEventListener('click', handleFooterClick);
        footerContainer.addEventListener('focusout', handleFooterFocusOut);
      }

      // Bind the scroll listener for this view; destroy() removes it. Passive
      // because the handler only reads scroll metrics and never preventDefaults.
      if (scrollHandler) {
        suggestionsScrollContainer.addEventListener('scroll', scrollHandler, {
          passive: true
        });
      }
      return {
        update: view => {
          const state = this.key && this.key.getState(view.state);
          if (state) {
            const hasNoSearchText = !state.text && opts.canSearch;
            const shouldNotSearch = !state.active && !opts.canSearch;
            if (hasNoSearchText || shouldNotSearch) {
              hideList();
              lastRange = {
                from: 0,
                to: 0
              };
              lastText = '';
            } else {
              const rangeChanged = state.range.from !== lastRange.from;
              const textChanged = state.text !== lastText;
              if (rangeChanged || textChanged) {
                dismissed = false;
                lastRange = state.range;
                lastText = state.text;
              }
              debouncedSearch(state, view);
            }
          }
        },
        destroy: () => {
          var _footerRoot;
          destroyed = true;
          if (typeof debouncedSearch.cancel === 'function') {
            debouncedSearch.cancel();
          }
          if (scrollHandler) {
            suggestionsScrollContainer.removeEventListener('scroll', scrollHandler);
          }
          if (footerContainer) {
            footerContainer.removeEventListener('mouseenter', handleFooterMouseEnter);
            footerContainer.removeEventListener('click', handleFooterClick);
            footerContainer.removeEventListener('focusout', handleFooterFocusOut);
          }
          (_footerRoot = footerRoot) === null || _footerRoot === void 0 || _footerRoot.unmount();
          footerRoot = undefined;
          el.remove();
        }
      };
    }
  });
}